import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { toast } from 'react-toastify';
import { useShallow } from 'zustand/react/shallow';

import { useMediaQuery } from '@mui/material';

import SocketService from '@src/services/SocketService';

import { useMediaPreview } from '@src/hooks';

import { TSelectSize } from '@src/shared/OSelect';
import { generatePeerInfo } from '@src/helpers';
import { getCustomErrorMessage, speechText } from '@src/utils';

import {
  useNotificationStore,
  usePeersStore,
  usePollsStore,
  useRecordingStore,
  useRoomActionsStore,
  useRoomConfigStore,
  useRoomStore,
  useVideoStreamStore,
  useWebinarRoomStore,
} from '@src/stores';

import { useRoomContext } from '@src/contexts';

import { EMeetingType, EUserType, ICurrentRecorder, IJoinResponse, IPoll } from '@src/types';
import { EWebinarRoutes, EWebinarSockets } from '@src/constants';
import { sendEventToWebView } from '@src/bridge/bridgeMessenger';
import { EWebViewEvents } from '@src/bridge/types';
import { hydrateSharedCalculator } from '@src/services/calculatorHydrator';

const useWebinarLobby = () => {
  const [isJoinLoading, setIsJoinLoading] = useState(false);
  const [password, setPassword] = useState('');
  const [joinErrors, setJoinErrors] = useState<Record<string, string>>({});

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const { setIsLoading } = useRoomContext();

  const { setIsJoinedRoom, roomTitle } = useRoomStore(
    useShallow((state) => ({
      setIsJoinedRoom: state.setIsJoinedRoom,
      roomTitle: state.roomTitle,
    }))
  );

  const { appendNotification } = useNotificationStore(
    useShallow((state) => ({
      appendNotification: state.appendNotification,
    }))
  );

  const { attendeeDetails, isWebinarStartedByHost } = useWebinarRoomStore(
    useShallow((state) => ({
      attendeeDetails: state.attendeeDetails,
      isWebinarStartedByHost: state.isWebinarStartedByHost,
    }))
  );

  const { setCurrentRecorder } = useRecordingStore(
    useShallow((state) => ({
      setCurrentRecorder: state.setCurrentRecorder,
    }))
  );

  const isSmallScreen = useMediaQuery('(max-width:1245px)');
  const isMediumScreen = useMediaQuery('(min-width:1246px) and (max-width:1500px)');

  const { webinarId, userId } = useMemo(() => {
    return {
      webinarId: searchParams.get('wid'),
      userId: searchParams.get('uid'),
    };
  }, [searchParams]);

  const { isHost, isPanelList, isPasswordRequired } = useMemo(() => {
    const userType = attendeeDetails?.user?.type;
    const isHost = userType === EUserType.HOST;
    const isCoHost = userType === EUserType.CO_HOST;
    const isSpeaker = userType === EUserType.SPEAKER;

    return {
      isHost,
      isCoHost,
      isSpeaker,
      isPanelList: isHost || isCoHost || isSpeaker,
      isPasswordRequired: attendeeDetails?.user?.passwordRequired,
    };
  }, [attendeeDetails]);

  const oSelectSize: TSelectSize = useMemo(() => {
    if (isSmallScreen) {
      return 'small';
    } else if (isMediumScreen) {
      return 'x-small';
    } else {
      return 'medium';
    }
  }, [isSmallScreen, isMediumScreen]);

  const {
    peerName,
    errors,
    inputValue,
    originalStream,
    isAudioAllowed,
    isVideoAllowed,
    speakerDevices,
    selectedAudio,
    selectedCamera,
    selectedSpeaker,
    videoInputDevices,
    audioInputDevices,
    handleAudio,
    handleVideo,
    handleChangeMediaDevice,
    handleChangeUsername,
    setVideoRef,
    isBackgroundEffectsOpen,
    isApplyButtonDisabled,
    selectedBackgroundEffect,
    isSubmitButtonDisabled,
    onSelectBackgroundEffect,
    handleOpenEffectsModal,
    removeBackgroundEffect,
    handleApplyChanges,
    handleCloseModal,
    isMicClosed,
    isCamClosed,
    isCamDisabled,
    isMicDisabled,
  } = useMediaPreview({ deniedPermissions: !isPanelList });

  const customTooltipStyle = {
    tooltip: {
      fontSize: '12px',
      padding: '6px 10px',
    },
  };

  const handleChangePassword = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { value } = e.target;
    setPassword(value);
    setJoinErrors({});
  };

  const handleJoinWebinar = useCallback(async () => {
    setJoinErrors({});
    setIsJoinLoading(true);
    const joinRequest = isPanelList ? EWebinarSockets.JOIN : EWebinarSockets.VIEWER_JOIN;
    let currentRecorder: ICurrentRecorder | null = null;

    try {
      const peerInfo = generatePeerInfo({
        peerName,
        peerAudio: !isMicClosed,
        peerVideo: !isCamClosed,
        peerImage: usePeersStore.getState().avatarUrl ?? '',
      });
      const response: IJoinResponse = await SocketService.request(joinRequest, {
        roomId: webinarId,
        userId,
        peerImage: usePeersStore.getState().avatarUrl ?? '',
        password,
        peerInfo,
      });

      if (!response.success) {
        const errorCode = response.errorCode ?? '';
        const message = response.message ?? 'Failed to join webinar';

        const customMessage = getCustomErrorMessage({ errorCode, message });
        switch (errorCode) {
          case 'WEBINAR_ENDED':
            navigate(EWebinarRoutes.END, { state: { roomType: 'webinar' }, replace: true });
            break;
          case 'FORBIDDEN':
            if (message.toLowerCase().includes('password')) {
              setJoinErrors({ password: 'Incorrect webinar password.' });
            } else {
              setJoinErrors({});
              toast.error(customMessage);
            }
            break;
          case 'CAPACITY_LIMIT':
            if (isPanelList) {
              navigate(EWebinarRoutes.FULL, { state: { attendeeDetails } });
            } else {
              navigate(EWebinarRoutes.WEBINAR_FULL_ROOM, { state: { attendeeDetails } });
            }
            break;
          case 'BANNED':
            navigate(EWebinarRoutes.RESTRICTED_ACCESS, { replace: true });
            break;
          default:
            setJoinErrors({});
            toast.error(customMessage);
            break;
        }

        return;
      }
      response?.accessKey && useRoomStore.getState().setAccessKey(response.accessKey);
      if (response.streamDetails) {
        useWebinarRoomStore.setState({ streamDetails: response.streamDetails });

        // Initialize viewer pins from server state
        const pinedUserIds = response.streamDetails?.agoraConverter?.pinedUserIds;
        if (pinedUserIds && Array.isArray(pinedUserIds)) {
          useWebinarRoomStore.getState().initializeViewerPinsFromServer(pinedUserIds);
        }
      }

      if (response.roomConfig) {
        useRoomConfigStore.setState({
          mainConfig: response.roomConfig.main,
          chatConfig: response.roomConfig.chat,
          pollConfig: response.roomConfig.poll,
          questionsConfig: response.roomConfig.questions,
          participantsListConfig: response.roomConfig.participantsList,
          magicPenConfig: response.roomConfig.magicPen,
        });
      }

      hydrateSharedCalculator(response.calculatorDetails, peerInfo?.peer_id);

      if (response.userAgoraStreamingDetails) {
        useWebinarRoomStore.setState({ agoraStreamingData: response.userAgoraStreamingDetails });
      }

      if (response.peers) {
        const peerEntries = response.peers.map((peer) => {
          const info = peer?.peer_info;

          if (!currentRecorder && info?.peer_recording === true && info?.peer_id && info?.peer_name) {
            currentRecorder = {
              peer_id: info.peer_id,
              peer_name: info.peer_name,
              action: 'Start Meeting recording',
            };
          }

          return [peer.id, peer] as const;
        });

        usePeersStore.getState().initPeers(new Map(peerEntries));
      }

      if (response.viewersCount) {
        usePeersStore.getState().setViewersCount(Number(response.viewersCount));
      }

      if (isPanelList) {
        try {
          await SocketService.request(EWebinarSockets.STREAM_START, { roomId: webinarId });
        } catch (err) {
          toast.error(String(err) || 'Failed to start stream');
        }
      } else if (response.user) {
        const { peer_id } = response.user.peer_info;
        usePeersStore.getState().appendPeer(peer_id, response.user);
      }

      if (response.hasPolls) {
        try {
          const response: { success: boolean; polls: IPoll[] } = await SocketService.request(EWebinarSockets.GET_POLLS);
          response.success && usePollsStore.getState().setPolls(response.polls);
        } catch (err) {
          console.error('Failed to fetch polls:', err);
        }
      }
      setIsJoinedRoom(true);
      setIsLoading(false);

      if (currentRecorder) {
        setCurrentRecorder(currentRecorder);
        useRoomActionsStore.getState().setScreenRecordingSettings({ isActive: true });
        appendNotification({
          title: 'Meeting Recording',
          text: 'This meeting is now starting to be recorded.',
          source: 'recording',
        });
        speechText('Start Meeting recording');
      } else {
        setCurrentRecorder({ peer_id: '', peer_name: '', action: '' });
        useRoomActionsStore.getState().setScreenRecordingSettings({ isActive: false });
      }
      // Send MINIMIZABLE_TOOLBAR event when user joins the webinar
      sendEventToWebView({ eventType: EWebViewEvents.MINIMIZABLE_TOOLBAR });
      sendEventToWebView({ eventType: EWebViewEvents.MEETING_STARTED });
      sendEventToWebView({ eventType: EWebViewEvents.IS_ATTENDEE, payload: { status: !isPanelList } });
    } catch (err) {
      toast.error(String(err) || 'Failed to join webinar');
    } finally {
      setIsJoinLoading(false);

      useVideoStreamStore.getState().setOriginalStream(null);
    }
  }, [
    isPanelList,
    peerName,
    isMicClosed,
    isCamClosed,
    webinarId,
    userId,
    password,
    setIsJoinedRoom,
    setIsLoading,
    navigate,
    attendeeDetails,
    setCurrentRecorder,
    appendNotification,
  ]);

  useEffect(() => {
    // TODO: we need to remove this flag from the code and only depends on the user type
    isHost && usePeersStore.setState({ isHostUser: true });

    useRoomStore.setState({
      meetingType: EMeetingType.WEBINAR,
      roomId: webinarId ?? '',
    });
  }, [isHost, webinarId]);

  useEffect(() => {
    const password = searchParams.get('pwd');
    if (!isHost && password) {
      setPassword(password);
    }
  }, [isHost, searchParams]);

  return {
    roomTitle,
    isHost,
    isPasswordRequired,
    isWebinarStartedByHost,
    errors,
    peerName,
    password,
    joinErrors,
    inputValue,
    oSelectSize,
    isSmallScreen,
    isMediumScreen,
    originalStream,
    isAudioAllowed,
    isVideoAllowed,
    speakerDevices,
    selectedAudio,
    selectedCamera,
    selectedSpeaker,
    videoInputDevices,
    audioInputDevices,
    handleVideo,
    handleAudio,
    handleChangeUsername,
    handleChangeMediaDevice,
    setVideoRef,
    isBackgroundEffectsOpen,
    isApplyButtonDisabled,
    selectedBackgroundEffect,
    isSubmitButtonDisabled,
    customTooltipStyle,
    handleCloseModal,
    handleApplyChanges,
    handleChangePassword,
    handleOpenEffectsModal,
    removeBackgroundEffect,
    onSelectBackgroundEffect,
    isMicClosed,
    isCamClosed,
    isJoinLoading,
    handleJoinWebinar,
    isCamDisabled,
    isMicDisabled,
  };
};

export default useWebinarLobby;
